import React, { useState, useMemo } from 'react';
import {
  LayoutDashboard, Users, Package, ShoppingCart, FileText,
  BarChart3, Mail, Settings, RefreshCw, Sparkles, Zap, ShieldCheck,
} from 'lucide-react';
import { useDashboard } from './useDashboard';
import { OverviewPage } from './pages/Overview';
import { CustomersPage } from './pages/Customers';
import { ProductsPage } from './pages/Products';
import { OrdersPage } from './pages/Orders';
import { ContentPage } from './pages/Content';
import { AnalyticsPage } from './pages/Analytics';
import { EmailsPage } from './pages/Emails';
import { SettingsPage } from './pages/Settings';

const NAV = [
  { id: 'overview',  label: 'Overview',  icon: LayoutDashboard, Page: OverviewPage },
  { id: 'customers', label: 'Customers', icon: Users,           Page: CustomersPage },
  { id: 'products',  label: 'Products',  icon: Package,         Page: ProductsPage },
  { id: 'orders',    label: 'Orders',    icon: ShoppingCart,    Page: OrdersPage },
  { id: 'content',   label: 'Content',   icon: FileText,        Page: ContentPage },
  { id: 'analytics', label: 'Analytics', icon: BarChart3,       Page: AnalyticsPage },
  { id: 'emails',    label: 'Emails',    icon: Mail,            Page: EmailsPage },
  { id: 'settings',  label: 'Settings',  icon: Settings,        Page: SettingsPage },
];

/* ============================================================================ */
/* DashboardShell — hardcoded layout. Theme injected via CSS variables.        */
/* ============================================================================ */

export default function DashboardShell({ chatId }) {
  const { status, dashboard, rows, regenerate, patch } = useDashboard(chatId);
  const [active, setActive] = useState('overview');

  // Build the CSS variable scope from the dashboard theme so colors / fonts
  // become per-project without touching any child component.
  const themeVars = useMemo(() => {
    const t = dashboard?.theme || {};
    return {
      '--dash-primary':      t.primary || '#7C3AED',
      '--dash-primary-text': t.primary_text || '#04221C',
      '--dash-bg':           t.bg || '#FBFAF7',
      '--dash-bg-card':      t.bg_card || '#FFFFFF',
      '--dash-text':         t.text || '#0F0F1E',
      '--dash-text-muted':   t.text_muted || '#6B6B7B',
      '--dash-border':       t.border || 'rgba(15,15,30,0.08)',
      '--dash-font-display': t.font_display || 'Inter, system-ui, sans-serif',
      '--dash-font-body':    t.font_body || 'Inter, system-ui, sans-serif',
      '--dash-radius':       t.radius || '14px',
    };
  }, [dashboard]);

  if (status === 'generating') {
    // Full "Aria is crafting" experience — happens only the very first time
    // a dashboard is created for this project. Cached forever afterwards.
    return <DashboardLoadingState />;
  }
  if (status === 'hydrating' && !dashboard) {
    // Silent fetch on a project we haven't seen this session yet — show a
    // discreet skeleton so the user never sees the "Aria is crafting" screen
    // for an existing dashboard.
    return <DashboardSkeleton />;
  }
  if (status === 'error') {
    return <DashboardErrorState onRetry={regenerate} />;
  }
  if (!dashboard) return null;

  const ActivePage = NAV.find(n => n.id === active)?.Page || OverviewPage;

  return (
    <div data-testid="dashboard-shell" style={{
      ...themeVars,
      display: 'flex', flex: 1, minHeight: 0,
      background: 'var(--dash-bg)',
      color: 'var(--dash-text)',
      fontFamily: 'var(--dash-font-body)',
    }}>
      {/* Left sidebar */}
      <aside style={{
        width: 220, flexShrink: 0,
        background: 'var(--dash-bg-card)',
        borderRight: '1px solid var(--dash-border)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Brand head */}
        <div style={{
          padding: '18px 18px 14px',
          borderBottom: '1px solid var(--dash-border)',
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'var(--dash-text-muted)',
            marginBottom: 4,
          }}>
            BTBA · Cockpit
          </div>
          <div style={{
            fontSize: 14, fontWeight: 700, color: 'var(--dash-text)',
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            fontFamily: 'var(--dash-font-display)',
          }}>
            {dashboard.title || 'Project'}
          </div>
          <ModeBadge mode={dashboard.mode} />
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
          {NAV.map(n => {
            const isActive = n.id === active;
            const Icon = n.icon;
            return (
              <button
                key={n.id}
                data-testid={`dash-nav-${n.id}`}
                onClick={() => setActive(n.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '8px 12px',
                  background: isActive ? 'var(--dash-primary)' : 'transparent',
                  color: isActive ? 'var(--dash-primary-text)' : 'var(--dash-text)',
                  border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: isActive ? 700 : 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                  marginBottom: 2, textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(15,15,30,0.04)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon size={15} />
                {n.label}
              </button>
            );
          })}
        </nav>

        {/* Footer mini stat */}
        <div style={{
          padding: '12px 18px',
          borderTop: '1px solid var(--dash-border)',
          fontSize: 11, color: 'var(--dash-text-muted)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={11} style={{ color: 'var(--dash-primary)' }} />
            DNA: {dashboard.dna?.vertical || 'generic'}
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <main style={{ flex: 1, overflowY: 'auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Sticky header strip — holds the Preview/Production toggle on every page */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          padding: '16px 36px 0',
          flexShrink: 0,
        }}>
          <ModeToggle
            mode={dashboard.mode}
            onChange={(m) => patch?.({ mode: m })}
          />
        </div>
        <div style={{ padding: '20px 36px 32px', flex: 1 }}>
          <ActivePage rows={rows} dashboard={dashboard} patch={patch} regenerate={regenerate} />
        </div>
      </main>
    </div>
  );
}

/* ============================================================================ */
/* Loading / error / banner states                                              */
/* ============================================================================ */

function DashboardLoadingState() {
  // Shown ONLY during the very first Aria generation for a project — never
  // again after the dashboard is persisted in MongoDB + the session cache.
  const steps = [
    'Scanning project DNA',
    'Detecting vertical & theme',
    'Generating sample data',
    'Wiring it all up',
  ];
  return (
    <div data-testid="dashboard-loading" style={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)',
      color: '#0F0F1E',
    }}>
      <div style={{ textAlign: 'center', maxWidth: 460 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '6px 14px', borderRadius: 999,
          background: 'rgba(124,58,237,0.10)',
          border: '1px solid rgba(124,58,237,0.30)',
          color: '#048264', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          marginBottom: 24,
        }}>
          <Sparkles size={12} />
          Aria is crafting your dashboard
        </div>
        <h2 style={{
          fontSize: 28, fontWeight: 700, margin: '0 0 12px',
          letterSpacing: '-0.02em',
        }}>
          Generating your dashboard…
        </h2>
        <p style={{ color: '#6B6B7B', fontSize: 14, marginBottom: 28 }}>
          Aria is reading your project, extracting its design DNA, and tailoring
          sample data so you see what it looks like once it's live.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {steps.map((s, i) => (
            <div key={s} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 14px', borderRadius: 10,
              background: 'rgba(255,255,255,0.7)',
              border: '1px solid rgba(15,15,30,0.06)',
              fontSize: 13, color: '#0F0F1E',
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: i === 1 ? '#7C3AED' : 'rgba(15,15,30,0.20)',
                animation: i === 1 ? 'pulse-dot 1.4s ease-in-out infinite' : 'none',
              }} />
              {s}
            </div>
          ))}
        </div>
        <style>{`@keyframes pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      </div>
    </div>
  );
}

/* ============================================================================ */
/* DashboardSkeleton — silent placeholder shown while we hydrate from backend  */
/* on a project we haven't seen this session yet. Never shown again afterwards */
/* (in-memory cache keeps the dashboard hot).                                   */
/* ============================================================================ */

function DashboardSkeleton() {
  const shimmer = {
    background: 'linear-gradient(90deg, rgba(15,15,30,0.05) 0%, rgba(15,15,30,0.08) 50%, rgba(15,15,30,0.05) 100%)',
    backgroundSize: '200% 100%',
    animation: 'dash-shimmer 1.4s ease-in-out infinite',
    borderRadius: 8,
  };
  return (
    <div data-testid="dashboard-skeleton" style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      background: '#FBFAF7', padding: '32px 36px',
    }}>
      <div style={{ ...shimmer, width: 180, height: 20, marginBottom: 14 }} />
      <div style={{ ...shimmer, width: 280, height: 14, marginBottom: 28, opacity: 0.7 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 24 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ ...shimmer, height: 110 }} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        <div style={{ ...shimmer, height: 220 }} />
        <div style={{ ...shimmer, height: 220 }} />
      </div>
      <style>{`@keyframes dash-shimmer {
        0% { background-position: 100% 0; }
        100% { background-position: -100% 0; }
      }`}</style>
    </div>
  );
}

function DashboardErrorState({ onRetry }) {
  return (
    <div data-testid="dashboard-error" style={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#FBFAF7', padding: 40, textAlign: 'center',
    }}>
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>
          Couldn't generate the dashboard
        </h2>
        <p style={{ color: '#6B6B7B', fontSize: 14, margin: '0 0 16px' }}>
          Something went wrong while crafting your cockpit.
        </p>
        <button
          data-testid="dashboard-retry"
          onClick={onRetry}
          style={{
            padding: '10px 18px', background: '#7C3AED',
            color: '#04221C', border: 'none', borderRadius: 10,
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <RefreshCw size={13} /> Try again
        </button>
      </div>
    </div>
  );
}

function ModeBadge({ mode }) {
  // Discrete pill rendered in the sidebar under the project title.
  const isPreview = mode !== 'live';
  return (
    <div style={{
      marginTop: 8,
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 999,
      background: isPreview ? 'rgba(255,176,32,0.14)' : 'rgba(124,58,237,0.14)',
      color: isPreview ? '#B47A00' : '#048264',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase',
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: '50%',
        background: isPreview ? '#FFB020' : '#7C3AED',
      }} />
      {isPreview ? 'Preview' : 'Production'}
    </div>
  );
}

/* ============================================================================ */
/* ModeToggle — segmented switch between Preview (Aria demo data) and          */
/* Production (real, live data from the deployed project).                     */
/* Bound to the dashboard's backend `mode` field: demo ↔ preview, live ↔ prod. */
/* ============================================================================ */

function ModeToggle({ mode, onChange }) {
  const isPreview = mode !== 'live';
  const baseBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px',
    fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
    border: 'none', borderRadius: 999,
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  };
  return (
    <div
      data-testid="dashboard-mode-toggle"
      title="Preview = Aria sample data · Production = live data from your deployed project"
      style={{
        display: 'inline-flex',
        padding: 4,
        background: 'rgba(15,15,30,0.05)',
        border: '1px solid var(--dash-border)',
        borderRadius: 999,
      }}
    >
      <button
        data-testid="mode-toggle-preview"
        onClick={() => onChange?.('demo')}
        style={{
          ...baseBtn,
          background: isPreview ? '#FFFFFF' : 'transparent',
          color: isPreview ? '#0F0F1E' : 'var(--dash-text-muted)',
          boxShadow: isPreview ? '0 1px 2px rgba(15,15,30,0.06), 0 2px 6px rgba(15,15,30,0.04)' : 'none',
        }}
      >
        <Zap size={12} fill={isPreview ? '#FFB020' : 'none'} stroke={isPreview ? '#FFB020' : 'currentColor'} />
        Preview
      </button>
      <button
        data-testid="mode-toggle-production"
        onClick={() => onChange?.('live')}
        style={{
          ...baseBtn,
          background: !isPreview ? 'var(--dash-primary)' : 'transparent',
          color: !isPreview ? 'var(--dash-primary-text)' : 'var(--dash-text-muted)',
          boxShadow: !isPreview ? '0 2px 8px rgba(124,58,237,0.28)' : 'none',
        }}
      >
        <ShieldCheck size={12} />
        Production
      </button>
    </div>
  );
}
